import { fetchJSON, postJSON } from '@purple-dot/libraries/src/fetch';

import {
  Errors,
  pollAsync,
} from '@purple-dot/libraries/src/language-helpers/promises/poll';

export const ERROR_MESSAGES = {
  CHECKOUT_SOLD_OUT:
    "We're sorry, this product sold out while we were processing your order. Any payment taken has been refunded",
  CHECKOUT_EXPIRED: 'This checkout session has expired',
  CHECKOUT_UNEXPECTED_ERROR:
    "We're sorry, an unexpected error occured while processing your order. Any payment taken has been refunded. Please refresh the page to try again or contact customer support.",
  CHECKOUT_TIMEOUT:
    "Please check your email for your order confirmation. If it doesn't arrive in the next few minutes, please contact customer support to check the status of your order.",
  PAYMENT_METHOD_MISMATCH:
    'Please use the same card details as the original preorder',
  TOO_MANY_PAYING:
    'Oops! A few too many people are trying to checkout. Please try again in 1 minute.',
  SHIPPING_GROUPS_TIMEOUT:
    'We encountered a problem whilst calculating your shipping rate. Please try again.',

  UNKNOWN_ERROR:
    "We're sorry, it looks like something has gone wrong with your request, please try again",
};

export class CheckoutStateError extends Error {
  constructor(message) {
    super(message);

    this.data = { isUserRetryable: false };
    this.originalMessage = message;
    this.responseSucceeded = true;
  }
}

export class CustomTimeoutError extends Error {
  constructor(message) {
    super(message ?? ERROR_MESSAGES.CHECKOUT_TIMEOUT);
    this.data = { isUserRetryable: false };
    this.originalMessage = ERROR_MESSAGES.CHECKOUT_TIMEOUT;
  }
}

export async function createCheckout([
  {
    storeId,
    skuId,
    presaleId,
    releaseId,
    lineItemProperties,
    userSessionToken,
  },
]) {
  try {
    const result = await postJSON(
      '/embedded-checkout/pre-order-checkouts/create',
      {
        storeId,
        skuId,
        presaleId,
        releaseId,
        lineItemProperties,
        userSessionToken,
      }
    );
    return result.data;
  } catch (err) {
    throw withDefaultedUnknownMessage(err);
  }
}

/**
 * Updates the checkout information.
 *
 * @param {Object[]} params - The parameters for updating the checkout information.
 * @param {string} params[].checkoutId - The ID of the checkout.
 * @param {string} params[].email - The email address of the customer.
 * @param {boolean} [params[].consentedToEmailMarketing] - Whether the customer consented to email marketing.
 * @param {boolean} [params[].consentedToSmsMarketing] - Whether the customer consented to SMS marketing.
 * @param {Object} params[].shippingAddress - The shipping address of the customer.
 * @param {boolean} params[].isPrefill - Whether the information is prefilled.
 * @returns {Promise<Object>} The result data from the API.
 * @throws Will throw an error if the request fails.
 */
export async function updateInformation([
  {
    checkoutId,
    email,
    consentedToEmailMarketing,
    consentedToSmsMarketing,
    shippingAddress,
    isPrefill,
  },
]) {
  try {
    const result = await postJSON(
      `/embedded-checkout/pre-order-checkouts/${checkoutId}`,
      {
        email,
        consentedToEmailMarketing,
        consentedToSmsMarketing,
        shippingAddress,
        isPrefill,
        clientTimestamp: Date.now(),
      }
    );
    return result.data;
  } catch (err) {
    throw withDefaultedUnknownMessage(err);
  }
}

export async function confirmShippingOptions([
  { checkoutId, selectedRates, preorderShippingOptionId },
]) {
  try {
    const result = await postJSON(
      '/embedded-checkout/pre-order-checkouts/confirm-shipping-options',
      {
        checkoutId,
        selectedRates,
        preorderShippingOptionId,
      }
    );
    return result.data;
  } catch (err) {
    throw withDefaultedUnknownMessage(err);
  }
}

export async function preferOriginalShipDate([
  { checkoutId, preferOriginalShipDate },
]) {
  try {
    const result = await postJSON(
      '/embedded-checkout/pre-order-checkouts/prefer-original-ship-date',
      {
        checkoutId,
        preferOriginalShipDate,
      }
    );
    return result.data;
  } catch (err) {
    throw withDefaultedUnknownMessage(err);
  }
}

/**
 * @returns {Promise<ReturnType<typeof import('../../../../core/checkouts/use-cases/set-pre-order-checkout-to-paying').SetPreorderCheckoutToPaying>>}
 */
export async function setCheckoutPaying([{ checkoutId, paymentMethodType }]) {
  try {
    const result = await postJSON(
      '/embedded-checkout/pre-order-checkouts/set-paying',
      { checkoutId, paymentMethodType }
    );
    return result.data;
  } catch (err) {
    if (err.statusCode === 503 || err.statusCode === 429) {
      err.originalMessage = ERROR_MESSAGES.TOO_MANY_PAYING;
      throw err;
    }
    throw withDefaultedUnknownMessage(err);
  }
}

export async function setCheckoutOpen([{ checkoutId }]) {
  try {
    const result = await postJSON(
      '/embedded-checkout/pre-order-checkouts/set-open',
      {
        checkoutId,
      }
    );
    return result.data;
  } catch (err) {
    throw withDefaultedUnknownMessage(err);
  }
}

export async function stopPaying([{ checkoutId }]) {
  try {
    const result = await postJSON(
      '/embedded-checkout/pre-order-checkouts/stop-paying',
      {
        checkoutId,
      }
    );
    return result.data;
  } catch (err) {
    throw withDefaultedUnknownMessage(err);
  }
}

export async function fetchCheckout([{ checkoutId }]) {
  const result = await fetchJSON(
    `/embedded-checkout/pre-order-checkouts/${checkoutId}`
  );
  return result.data;
}

async function fetchCheckoutState({ checkoutId, addToPreorderCheckout }) {
  const checkout = await fetchCheckout([{ checkoutId }]);

  const preorderCompleted = !!checkout.preorder;
  const inStockCompleted = !checkout.hasRegularLineItems || !!checkout.order;
  const { paymentCompleted } = checkout;

  let { state } = checkout;
  // Don't consider the checkout complete until both orders have been completed
  if (
    (state === 'PAYING' || state === 'COMPLETED') &&
    paymentCompleted &&
    !inStockCompleted &&
    !addToPreorderCheckout
  ) {
    state = 'RECEIVED';
  }

  return {
    state,
    error: checkout.error,
    preorderCompleted,
    inStockCompleted,
    paymentCompleted,
  };
}

const SIX_MINUTES = 360000;
const TWENTY_SECONDS = 20000;

export async function waitForCheckoutCompleted({
  checkoutId,
  completedTimeout = SIX_MINUTES,
  receivedTimeout = TWENTY_SECONDS,
  addToPreorderCheckout = false,
}) {
  try {
    const result = await pollAsync(
      async (timeSpent) => {
        try {
          const { state, error, ...completed } = await fetchCheckoutState({
            checkoutId,
            addToPreorderCheckout,
          });

          if (
            state === 'RECEIVED' &&
            receivedTimeout >= 0 &&
            timeSpent > receivedTimeout
          ) {
            const data = await fetchCheckout([{ checkoutId }]);
            return {
              stopPolling: true,
              result: { ...data, state, ...completed },
            };
          }

          if (error === 'PAYMENT_METHOD_MISMATCH') {
            throw new CheckoutStateError(
              ERROR_MESSAGES.PAYMENT_METHOD_MISMATCH
            );
          }

          if (state === 'PAYING' || state === 'RECEIVED') {
            return { stopPolling: false };
          }

          if (state === 'COMPLETED' || state === 'OPEN') {
            const data = await fetchCheckout([{ checkoutId }]);
            return { stopPolling: true, result: { ...data, ...completed } };
          }

          if (state === 'SOLD_OUT') {
            throw new CheckoutStateError(ERROR_MESSAGES.CHECKOUT_SOLD_OUT);
          }

          if (state === 'EXPIRED') {
            throw new CheckoutStateError(ERROR_MESSAGES.CHECKOUT_EXPIRED);
          }

          // state === ERROR
          throw new CheckoutStateError(
            ERROR_MESSAGES.CHECKOUT_UNEXPECTED_ERROR
          );
        } catch (err) {
          // Keep polling if there's a network error
          if (!err.responseSucceeded) {
            return { stopPolling: false, result: false };
          }
          throw withDefaultedUnknownMessage(err);
        }
      },
      (polledTimes) => {
        // After ~2 minutes
        if (polledTimes > 30) {
          return 10000;
        }

        // After ~45 seconds
        if (polledTimes > 15) {
          return 5000;
        }

        // After ~20 seconds
        if (polledTimes > 10) {
          return 3000;
        }
        // After ~4 seconds
        if (polledTimes > 5) {
          return 1500;
        }

        return 750;
      },
      completedTimeout
    );
    return result;
  } catch (err) {
    if (err instanceof Errors.PollingTimeoutError) {
      throw new CustomTimeoutError();
    }
    throw err;
  }
}

export async function addItemToCheckout([
  { checkoutId, skuId, presaleId, releaseId },
]) {
  const result = await postJSON('/embedded-checkout/pre-order-checkouts/add', {
    checkoutId,
    skuId,
    presaleId,
    releaseId,
  });
  return result.data;
}

export async function removeItemFromCheckout([{ checkoutId, lineItemId }]) {
  const result = await postJSON(
    '/embedded-checkout/pre-order-checkouts/remove',
    {
      checkoutId,
      lineItemId,
    }
  );
  return result.data;
}

export async function addDiscountCodeToCheckout([{ checkoutId, code }]) {
  const result = await postJSON(
    '/embedded-checkout/pre-order-checkouts/add-discount-code',
    {
      checkoutId,
      code,
    }
  );
  return result.data;
}

export async function getAddToPreorderView([{ checkoutId }]) {
  const result = await fetchJSON(
    `/embedded-checkout/add-to-preorder/view?checkoutId=${checkoutId}`
  );

  return result.data;
}

export async function removeDiscountCodeFromCheckout([
  { checkoutId, discountApplicationId },
]) {
  const result = await postJSON(
    '/embedded-checkout/pre-order-checkouts/remove-discount-code',
    {
      checkoutId,
      discountApplicationId,
    }
  );
  return result.data;
}

export async function removeGiftCardPaymentFromCheckout([
  { checkoutId, paymentIntentId },
]) {
  const result = await postJSON(
    '/embedded-checkout/pre-order-checkouts/remove-gift-card-payment',
    {
      checkoutId,
      paymentIntentId,
    }
  );
  return result.data;
}

export async function fetchAvailableShippingRates([{ checkoutId }]) {
  const result = await fetchJSON(
    `/embedded-checkout/pre-order-checkouts/${checkoutId}/shipping-rates`
  );
  return result.data;
}

export async function fetchShippingOptions([{ checkoutId }]) {
  try {
    const result = await fetchJSON(
      `/embedded-checkout/pre-order-checkouts/${checkoutId}/shipping-group-options`
    );
    return result.data;
  } catch (err) {
    if (err.statusCode === 503 || err.statusCode === 429) {
      throw new CheckoutStateError(ERROR_MESSAGES.SHIPPING_GROUPS_TIMEOUT);
    }
    throw withDefaultedUnknownMessage(err);
  }
}

export async function setShippingGroupOptions({
  checkoutId,
  option,
  selectedShippingGroupRates,
  confirm,
  preferOriginalShipDate,
}) {
  const result = await postJSON(
    `/embedded-checkout/pre-order-checkouts/${checkoutId}/shipping-group-options`,
    {
      option,
      confirm,
      selectedShippingRates:
        selectedShippingGroupRates &&
        Object.entries(selectedShippingGroupRates).map(
          ([shippingGroupId, rateId]) => ({ shippingGroupId, rateId })
        ),
      clientTimestampMs: Date.now(),
      preferOriginalShipDate,
    }
  );
  return result.data;
}

function withDefaultedUnknownMessage(err) {
  err.originalMessage = err.originalMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
  return err;
}
